import { LabelledField } from "@mixitone/components";
import BracketIcon from "@mixitone/icons/bracket.svg";
import EditIcon from "@mixitone/icons/edit.svg";
import GridSectionsIcon from "@mixitone/icons/grid-sections.svg";
import GridIcon from "@mixitone/icons/grid.svg";
import PlayerIcon from "@mixitone/icons/player.svg";
import RobinIcon from "@mixitone/icons/round-robin.svg";
import { Account, TournamentCompetition } from "@mixitone/models";
import { entries, withBatch, withStateToggle } from "@mixitone/util";
import { FormController } from "components/Forms/FormController";
import { useRef } from "react";
import { type FormatName, formats, getFormat } from "tournament-formats";
import type { FormatSchema, SchemaField, SchemaSettings } from "tournament-formats/TournamentFormat";
import type { FormFieldConfig } from "./FormFieldsDefinition";
import { ValueComponent } from "./FormValueComponents";
import DivisionSplittingRulesField from "./DivisionSplittingRulesField";

interface State {
  formatSettings: any;
  formatSchema: FormatSchema;
}

export class TournamentCompetitionFormController extends FormController<
  TournamentCompetition,
  {
    tournament_session_id: string;
  },
  State
> {
  override async buildRecord(): Promise<TournamentCompetition> {
    return new TournamentCompetition({
      ...this.props.attributes,
      status: "setup",
      min_entries: 2,
      max_entries: 100,
      format_settings: this.state.formatSettings,
    });
  }

  fields() {
    return {
      name: {
        name: "name",
        type: "text",
        label: "Name",
        required: true,
        placeholder: "e.g. Mixed doubles",
        group: "basic",
      },

      entry_type: {
        name: "entry_type",
        type: "select",
        label: "Entry type",
        required: true,
        group: "basic",
        options: [
          { label: "Singles", value: "singles", icon: <PlayerIcon /> },
          {
            label: "Doubles",
            value: "doubles",
            icon: (
              <>
                <PlayerIcon />
                <PlayerIcon className="-m-4 -translate-x-2" />
              </>
            ),
          },
        ],
      },

      min_entries: {
        name: "min_entries",
        type: "number",
        label: "Minimum entries",
        required: true,
        group: "limits",
        min: 2,
      },

      max_entries: {
        name: "max_entries",
        type: "number",
        label: "Target entries",
        required: true,
        group: "limits",
        min: 2,
        description: "Soft goal for signups. Entries can go over this.",
      },

      format: {
        name: "format",
        type: "select",
        label: "Format",
        required: true,
        group: "format",
        options: entries(formats).map(([key, format]) => {
          let icon: React.ReactNode;
          switch (key) {
            case "SingleElimination":
              icon = <BracketIcon />;
              break;
            case "Swiss":
              icon = <GridIcon />;
              break;
            case "SwissWithDivisions":
              icon = <GridSectionsIcon />;
              break;
            case "RoundRobin":
              icon = <RobinIcon />;
              break;
            case "Custom":
              icon = <EditIcon />;
              break;
          }

          return {
            label: format.getName(),
            description: format.description,
            value: key,
            icon,
          };
        }),
        onChange: (value: FormatName) => {
          this.state.record.format = value;
          this.state.formatSchema = getFormat(value).getSettingsSchema();
        },
      },
    } as const satisfies Record<string, FormFieldConfig>;
  }

  /**
   * Validate format settings before saving
   */
  validate(): string[] {
    const errors: string[] = [];
    const minEntries = this.state.record.min_entries ?? 2;
    const targetEntries = this.state.record.max_entries ?? 100;

    if (minEntries < 2) {
      errors.push("Minimum entries must be at least 2");
    }

    if (targetEntries < minEntries) {
      errors.push("Target entries must be at least the minimum entries");
    }

    return errors;
  }

  /**
   * Load format schema and settings for an existing competition.
   * This ensures the form shows the correct configuration fields
   * and preserves any custom settings.
   */
  override async onLoad() {
    await super.onLoad();

    if (!this.state.record.format || !(this.state.record.format in formats)) {
      this.state.record.format = "SingleElimination";
    }

    const format = getFormat((this.state.record.format as FormatName) ?? "SingleElimination");
    this.state.formatSchema = format.getSettingsSchema();
    this.state.formatSettings = this.state.record.format_settings ?? {};
  }

  /**
   * Handle format selection changes.
   * This updates both the competition format and loads the appropriate
   * schema and default settings for the selected format.
   */
  @withBatch
  async actionChangeFormat(name: FormatName) {
    const format = getFormat(name);
    this.state.record.format = name;
    this.state.formatSchema = format.getSettingsSchema();
  }

  override findModel(id: string) {
    return TournamentCompetition.query().find(id);
  }

  /**
   * Save the competition record with proper error handling
   */
  @withStateToggle("saving")
  override async saveRecord(competition: TournamentCompetition) {
    try {
      competition.account_id = Account.current!.id;
      competition.format_settings = this.state.formatSettings;
      await competition.save();
    } catch (error) {
      // Handle specific validation errors
      if (error instanceof Error) {
        if (error.message.includes("format_settings")) {
          throw new Error("Invalid format settings");
        }
        throw error;
      }
      throw error;
    }
  }

  get canDelete(): boolean {
    return true;
  }

  deleteMessage() {
    const entries = this.state.record.entries.count();
    return `Are you sure you want to delete this competition? This will also remove ${entries} entries.`;
  }
}

/**
 * Converts a schema field to a form field configuration
 */
const schemaFieldToFormConfig = (name: string, field: SchemaField): FormFieldConfig => {
  // Always provide a fallback description to satisfy the label requirement
  const label = field.description || name;

  switch (field.type) {
    case "string":
      return {
        name,
        type: "text" as const,
        label,
        required: false,
        placeholder: "",
        minLength: undefined,
        maxLength: undefined,
      };
    case "number":
      return {
        name,
        type: "number" as const,
        label,
        required: false,
        placeholder: "",
        min: undefined,
        max: undefined,
      };
    case "boolean":
      return {
        name,
        type: "checkbox" as const,
        label,
        required: false,
        text: field.description,
      };
    default:
      return {
        name,
        type: "text" as const,
        label,
        required: false,
        placeholder: "",
      };
  }
};

const FormatSettingsField = <
  N extends string,
  F extends SchemaField,
  S extends SchemaSettings<Record<string, SchemaField>>,
>({
  name,
  field,
  settings,
  onFieldChange,
}: {
  name: N;
  field: F;
  settings: S;
  onFieldChange: (name: N, value: string | number | boolean) => void;
}) => {
  if (field.hidden) {
    return null;
  }

  const fieldConfig = {
    ...schemaFieldToFormConfig(name, field),
    onChange(value: any) {
      onFieldChange(name, value);
    },
    value() {
      return settings[name] ?? field.default ?? (field.type === "boolean" ? false : "");
    },
  } as FormFieldConfig;

  return (
    <LabelledField label={field.description} htmlFor={name}>
      <ValueComponent field={fieldConfig} record={settings} />
    </LabelledField>
  );
};
/**
 * Renders format-specific configuration fields based on the format's schema.
 * - For SingleEliminationFormat: Shows division configuration
 * - For RoundRobinFormat: Shows group size configuration
 */
/**
 * Format settings form fields component.
 * Renders configuration fields based on the selected format's schema.
 * Handles validation and updates through the provided onChange callback.
 *
 * Field updates merge against a ref of the latest settings so rapid successive
 * edits (e.g. Playwright fills) do not clobber earlier keys via stale props.
 */
export const FormatSettingsFields: React.FC<{
  schema: FormatSchema;
  settings: SchemaSettings<any>;
  onChange: (settings: SchemaSettings<any>) => void;
}> = ({ schema, settings, onChange }) => {
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  const onFieldChange = (name: string, value: string | number | boolean) => {
    const next: SchemaSettings<any> = { ...settingsRef.current, [name]: value };
    settingsRef.current = next;
    onChange(next);
  };

  return (
    <>
      {Object.entries(schema).map(([key, field]) => {
        // Special handling for division splitting rules in RoundRobin format
        // Show this field when divisions > 1 and playoffs are enabled
        if (key === "divisionSplittingRules") {
          const divisions = (settings.divisions as number) || 1;
          const enablePlayoffs = (settings.enablePlayoffs as boolean) || false;
          const qualifiersPerGroup = (settings.qualifiersPerGroup as number) || 1;

          // Only show if playoffs are enabled and there are multiple divisions
          if (!enablePlayoffs || divisions <= 1) {
            return null;
          }

          const value = (settings[key] as string) || "";

          return (
            <LabelledField key={key} label="Division Splitting Rules" htmlFor={key}>
              <DivisionSplittingRulesField
                value={value}
                onChange={(newValue) => onFieldChange(key, newValue)}
                divisions={divisions}
                qualifiersPerGroup={qualifiersPerGroup}
              />
            </LabelledField>
          );
        }

        return (
          <FormatSettingsField
            key={key}
            name={key}
            field={field}
            settings={settings}
            onFieldChange={onFieldChange}
          />
        );
      })}
    </>
  );
};
